import React, { useState, useRef, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  ThemeProvider,
  createTheme,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ButtonGroup,
  Divider,
  Toolbar,
  Snackbar,
  Alert,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { generateQuotationPDF } from "./pdfGenerator";
import { useUserContext } from './context/UserContext';
import { doc, getDoc, updateDoc, increment, collection, addDoc, Timestamp, runTransaction } from "firebase/firestore";
import { db } from "../firebase.config";
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  Create,
  Close,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { QuotationContext } from "./context/QuotationContext";

// --- Helper Components ---
const SignatureUploadDialog = ({ open, onClose, onSave }) => {
  const [name, setName] = useState('');
  const [imageSrc, setImageSrc] = useState(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('File is too large. Please select an image under 5MB.');
      setImageSrc(null);
      return;
    }

    setError('');
    const reader = new FileReader();
    reader.onload = (e) => {
      setImageSrc(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = () => {
    if (!imageSrc) {
      alert("Please upload a signature image first.");
      return;
    }

    const img = new Image();
    img.src = imageSrc;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      const maxWidth = 400;
      const maxHeight = 200;
      let width = img.width;
      let height = img.height;

      if (width > height) {
        if (width > maxWidth) {
          height *= maxWidth / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width *= maxHeight / height;
          height = maxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);
      
      const compressedImageData = canvas.toDataURL('image/png', 0.9);

      onSave(compressedImageData, name);
      handleClose();
    };
    img.onerror = () => {
        alert("Failed to load the image for processing. Please try a different image file.");
    }
  };
  
  const handleClose = () => {
    setImageSrc(null);
    setName('');
    setError('');
    if (fileInputRef.current) {
        fileInputRef.current.value = "";
    }
    onClose();
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Upload Signature Image</DialogTitle>
      <DialogContent>
        <Button variant="contained" component="label" fullWidth sx={{ mb: 2 }}>
          Select Image
          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept="image/png, image/jpeg"
            onChange={handleFileChange}
          />
        </Button>
        {error && <Typography color="error" variant="body2" sx={{ mb: 2, textAlign: 'center' }}>{error}</Typography>}
        {imageSrc && (
          <Box sx={{ border: '1px dashed #ccc', borderRadius: 1, mb: 2, p: 1, textAlign: 'center' }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Preview:</Typography>
            <img 
              src={imageSrc} 
              alt="Signature Preview" 
              style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }} 
            />
          </Box>
        )}
        <TextField
          label="Name / Title"
          fullWidth
          value={name}
          onChange={e => setName(e.target.value)}
          variant="outlined"
          size="small"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={!imageSrc}>Save Signature</Button>
      </DialogActions>
    </Dialog>
  );
};

const theme = createTheme({
  palette: {
    primary: { main: "#3f51b5" },
    secondary: { main: "#dc004e" },
  },
  typography: {
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    h4: { fontWeight: 700 },
    h6: { fontWeight: 600 },
  },
});

const formatCurrency = (amount, symbol = "₹") => {
  const num = parseFloat(amount);
  if (isNaN(num)) {
    return `${symbol}0.00`;
  }
  return `${symbol}${num.toFixed(2)}`;
};

// --- Main QTemplate Component ---
const QTemplate = () => {
  // MODIFIED: Use quotation-specific variables from context
  const {
    userProfile,
    quotationLogo,
    quotationBanner,
    quotationBusinessName,
    quotationBusinessAddress,
    quotationDefaultTermsAndConditions,
    quotationDefaultAdditionalNotes,
    quotationDefaultAuthorizedSignature,
    quotationDefaultAuthorizedSignatureName,
    userId,
    quotationValidityDays,
    currency,
    quotationFooter,
    quotationIdPrefix,
    quotationCustomCounter,
    quotationPrefixSetAt,
  } = useUserContext();

  const getCurrencySymbol = () => {
    let symbol = '₹';
    if (typeof currency === 'object' && currency !== null && typeof currency.symbol === 'string' && currency.symbol.length > 0) {
        symbol = currency.symbol;
    } else if (typeof currency === 'string' && currency.length > 0) {
        symbol = currency;
    }
    if (!isNaN(parseFloat(symbol)) && isFinite(symbol)) {
        return '$';
    }
    return symbol;
  };
  const currencySymbol = getCurrencySymbol();

  const navigate = useNavigate();
  const { selectedItems, draft, setDraft, clearSelectedItems } = useContext(QuotationContext);
  
  const [quotationNumber, setQuotationNumber] = useState("");
  const [billTo, setBillTo] = useState({ name: "", address: "" });
  const [shipTo, setShipTo] = useState({ name: "", address: "" });
  
  const [quotationDate, setQuotationDate] = useState(new Date().toISOString().split('T')[0]);
  const [validUntil, setValidUntil] = useState('');
  const [items, setItems] = useState([
    { description: "", unitPrice: 0, quantity: 0, discount: 0, tax: 0, total: 0, isLocked: false, sourceId: null },
  ]);

  const [additionalNotes, setAdditionalNotes] = useState(quotationDefaultAdditionalNotes || "");
  const [termsAndConditions, setTermsAndConditions] = useState(quotationDefaultTermsAndConditions || 'No terms and conditions specified.');
  
  const [isPrinting, setIsPrinting] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [quotationId, setQuotationId] = useState(null);
  const [templateId, setTemplateId] = useState(6);

  const [coveringLetter, setCoveringLetter] = useState("");
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [currentSignatureType, setCurrentSignatureType] = useState("");
  const [authorizedSignature, setAuthorizedSignature] = useState(quotationDefaultAuthorizedSignature || "");
  const [authorizedSignatureName, setAuthorizedSignatureName] = useState(quotationDefaultAuthorizedSignatureName || "");
  const [customerSignature, setCustomerSignature] = useState("");
  const [customerSignatureName, setCustomerSignatureName] = useState("");

  const contentRef = useRef(null);
  const editorRef = useRef(null);

  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success' // 'success', 'error', 'warning', 'info'
  });

  const showNotification = (message, severity = 'success') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  const handleCloseNotification = (event, reason) => {
    if (reason === 'clickaway') return;
    setNotification({ ...notification, open: false });
  };

  useEffect(() => {
    if (draft && !quotationId) {
        setQuotationNumber(draft.quotationNumber || "");
        setBillTo(draft.billTo || { name: "", address: "" });
        setShipTo(draft.shipTo || { name: "", address: "" });
        setQuotationDate(draft.quotationDate || new Date().toISOString().split('T')[0]);
        setValidUntil(draft.validUntil || '');
        setItems(draft.items || [{ description: "", unitPrice: 0, quantity: 0, discount: 0, tax: 0, total: 0, isLocked: false }]);
        setAdditionalNotes(draft.additionalNotes || quotationDefaultAdditionalNotes || "");
        setTermsAndConditions(draft.termsAndConditions || quotationDefaultTermsAndConditions || 'No terms and conditions specified.');
        setCoveringLetter(draft.coveringLetter || "");
        setAuthorizedSignature(draft.authorizedSignature || quotationDefaultAuthorizedSignature || "");
        setAuthorizedSignatureName(draft.authorizedSignatureName || quotationDefaultAuthorizedSignatureName || "");
        setCustomerSignatureName(draft.customerSignatureName || "");
        if (editorRef.current) {
            editorRef.current.innerHTML = draft.coveringLetter || '';
        }
    }
  }, [draft, quotationId, quotationDefaultTermsAndConditions, quotationDefaultAdditionalNotes, quotationDefaultAuthorizedSignature, quotationDefaultAuthorizedSignatureName]);

  useEffect(() => {
    if (selectedItems && selectedItems.length > 0) {
      const newItemsFromSelection = selectedItems.map(item => {
        const quantity = parseFloat(item.moq || item.quantity || 1);
        const unitPrice = parseFloat(item.price) || 0;
        const tax = parseFloat(item.gst) || 0;
        const discount = parseFloat(item.discount) || 0;
        // Preserve the original cost entered in AddProduct (if present)
        const cost = item.cost !== undefined ? (parseFloat(item.cost) || 0) : 0;

        const discountedPrice = unitPrice * (1 - (discount / 100));
        const total = quantity * discountedPrice * (1 + (tax / 100));

        return {
          description: item.name || '',
          unitPrice,
          quantity,
          discount,
          tax,
          total,
          isLocked: true,
          sourceId: null,
          cost,
        };
      });

      setItems(prevItems => {
        const existingValidItems = prevItems.filter(
          item => item.description || item.quantity > 0 || item.unitPrice > 0
        );
        return [...existingValidItems, ...newItemsFromSelection];
      });

      if (clearSelectedItems) {
        clearSelectedItems();
      }
      setHasUnsavedChanges(true);
    }
  }, [selectedItems, clearSelectedItems]);
  
  useEffect(() => {
    if (quotationDate) {
        const date = new Date(quotationDate);
        const days = parseInt(quotationValidityDays, 10) || 7;
        date.setDate(date.getDate() + days);
        setValidUntil(date.toISOString().split('T')[0]);
        setHasUnsavedChanges(true);
    }
  }, [quotationDate, quotationValidityDays]);

  useEffect(() => {
    if (!isSaved) {
      const digitLength = quotationCustomCounter.length || 5;
      const hashmarks = '#'.repeat(digitLength);
      const prefix = quotationIdPrefix.padEnd(4, 'X').slice(0, 4).toUpperCase();
      setQuotationNumber(prefix ? prefix + '-' + hashmarks : hashmarks);
    }
  }, [quotationIdPrefix, isSaved, quotationCustomCounter]);

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...items];
    const currentItem = { ...updatedItems[index], [field]: value };
  
    const quantity = parseFloat(currentItem.quantity) || 0;
    const unitPrice = parseFloat(currentItem.unitPrice) || 0;
    const discount = parseFloat(currentItem.discount) || 0;
    const tax = parseFloat(currentItem.tax) || 0;
  
    const discountedPrice = unitPrice * (1 - (discount / 100));
    const finalTotal = quantity * discountedPrice * (1 + (tax / 100));
  
    updatedItems[index] = { ...currentItem, total: finalTotal };
  
    setItems(updatedItems);
    setHasUnsavedChanges(true);
  };

  const addItem = () => {
    setItems([...items, { description: "", unitPrice: 0, quantity: 0, discount: 0, tax:0, total: 0, isLocked: false, sourceId: null }]);
    setHasUnsavedChanges(true);
  };

  const deleteItem = (index) => {
    if (items.length <= 1) {
      setItems([{ description: "", unitPrice: 0, quantity: 0, discount: 0, tax: 0, total: 0, isLocked: false, sourceId: null }]);
    } else {
      const updatedItems = items.filter((_, i) => i !== index);
      setItems(updatedItems);
    }
    setHasUnsavedChanges(true);
  };
  
  const execCommand = (command) => document.execCommand(command, false, null);
  const handleEditorInput = () => {
    if (editorRef.current) {
        setCoveringLetter(editorRef.current.innerHTML);
        setHasUnsavedChanges(true);
    }
  };

  const totals = React.useMemo(() => {
    let subtotal = 0;
    let totalDiscountAmount = 0;
    let totalTaxAmount = 0;

    items.forEach(item => {
        const quantity = parseFloat(item.quantity) || 0;
        const unitPrice = parseFloat(item.unitPrice) || 0;
        const discountPercent = parseFloat(item.discount) || 0;
        const taxPercent = parseFloat(item.tax) || 0;

        const lineSubtotal = quantity * unitPrice;
        const lineDiscountAmount = lineSubtotal * (discountPercent / 100);
        const taxableAmountForLine = lineSubtotal - lineDiscountAmount;
        const lineTaxAmount = taxableAmountForLine * (taxPercent / 100);

        subtotal += lineSubtotal;
        totalDiscountAmount += lineDiscountAmount;
        totalTaxAmount += lineTaxAmount;
    });

    const taxableAmount = subtotal - totalDiscountAmount;
    const grandTotal = taxableAmount + totalTaxAmount;

    return {
      subtotal: subtotal,
      totalDiscountAmount: totalDiscountAmount,
      taxableAmount: taxableAmount,
      totalTaxAmount: totalTaxAmount,
      grandTotal: grandTotal,
      cgst: totalTaxAmount / 2,
      sgst: totalTaxAmount / 2,
      igst: totalTaxAmount
    };
  }, [items]);

  const generateQuotationId = (prefix, nextNumber) => {
    const digitLength = quotationCustomCounter.length || 5;
    const safePrefix = prefix.padEnd(4, 'X').slice(0, 4).toUpperCase();
    return safePrefix + '-' + String(nextNumber).padStart(digitLength, '0');
  };

  const resetForm = () => {
    setBillTo({ name: '', address: '' });
    setShipTo({ name: '', address: '' });
    setAdditionalNotes(quotationDefaultAdditionalNotes || '');
    setTermsAndConditions(quotationDefaultTermsAndConditions || 'No terms and conditions specified.');
    setQuotationDate(new Date().toISOString().split('T')[0]);
    setCoveringLetter("");
    if(editorRef.current) editorRef.current.innerHTML = "";
    setAuthorizedSignature(quotationDefaultAuthorizedSignature || "");
    setAuthorizedSignatureName(quotationDefaultAuthorizedSignatureName || "");
    setCustomerSignatureName("");
    setQuotationNumber("");
    setItems([{ description: "", unitPrice: 0, quantity: 0, discount: 0, tax: 0, total: 0, isLocked: false, sourceId: null }]);
    setHasUnsavedChanges(true);
    setIsSaved(false);
    setQuotationId(null);
  };
  
  const handleClick = () => {
    const currentDraft = {
      quotationNumber,
      billTo,
      shipTo,
      quotationDate,
      validUntil,
      items,
      additionalNotes,
      termsAndConditions,
      coveringLetter,
      authorizedSignature,
      authorizedSignatureName,
      customerSignatureName,
    };
    setDraft(currentDraft);
    navigate("/select-quotation-products", { state: { from: window.location.pathname } });
  };

  const openSignatureModal = (type) => {
    setCurrentSignatureType(type);
    setShowSignatureModal(true);
  };
  const handleCloseSignatureModal = () => {
    setShowSignatureModal(false);
    setCurrentSignatureType("");
  };
  const addSignature = (signature, name) => {
    if (currentSignatureType === "authorized") {
      setAuthorizedSignature(signature);
      setAuthorizedSignatureName(name);
    } else if (currentSignatureType === "customer") {
      setCustomerSignature(signature);
    }
    handleCloseSignatureModal();
    setHasUnsavedChanges(true);
  };

  const saveQuotation = async () => {
    // Check if prefix or counter hasn't been set for more than 1 year
    const oneYearInMs = 365 * 24 * 60 * 60 * 1000;
    const now = new Date();
    
    // Check prefix
    if (quotationPrefixSetAt) {
      const prefixSetDate = new Date(quotationPrefixSetAt);
      if (now - prefixSetDate > oneYearInMs) {
        showNotification("⚠️ Your quotation prefix hasn't been updated in over 1 year. Please contact the administrator.", 'warning');
        return;
      }
    }
    
    // Check counter
    if (!quotationCustomCounter) {
      showNotification("⚠️ Quotation counter ID has not been set. Please contact the administrator.", 'warning');
      return;
    }
    
    if (!billTo.name || !billTo.address || !quotationDate || !validUntil) {
      showNotification("Please fill in all required fields (Client Name, Address, Dates).", 'warning');
      return;
    }
    // Only validate items if they are not empty
    const hasItems = items.some(item => item.description || item.quantity > 0 || item.unitPrice > 0);
    if (hasItems) {
      const allItemsFilled = items.every(item => !item.description || (item.quantity > 0 && item.unitPrice >= 0));
      if (!allItemsFilled) {
        showNotification("Please ensure every item has a quantity and unit price greater than zero.", 'warning');
        return;
      }
    }
    
    if (!userProfile || !userProfile.tenantId) {
        showNotification("Could not find Tenant ID. User profile may not be loaded correctly. Please try again.", 'error');
        return;
    }
  
    try {
        await runTransaction(db, async (transaction) => {
            const quotationCountRef = doc(db, "quotations", "quotationcount");
            const quotationCountSnap = await transaction.get(quotationCountRef);
            
            let currentQuotationNumber = 0;
            if (!quotationCountSnap.exists()) {
                // Create the document if it doesn't exist
                transaction.set(quotationCountRef, { quotationnumber: 0 });
            } else {
                currentQuotationNumber = quotationCountSnap.data().quotationnumber;
            }
            
            const nextNumber = currentQuotationNumber + 1;
            const newQuotationNumber = generateQuotationId(quotationIdPrefix, nextNumber);
            
            const tenantId = userProfile.tenantId;
            
            // MODIFIED: Use quotation-specific variables
            const quotationData = {
                quotationId: newQuotationNumber,
                user: userId,
                billTo,
                shipTo,
                quotationDate,
                validUntil,
                items,
                ...totals,
                additionalNotes,
                termsAndConditions,
                templateId,
                currencySymbol,
                coveringLetter,
                logo: quotationLogo || '',
                banner: quotationBanner || '',
                authorizedSignature: authorizedSignature || '',
                authorizedSignatureName: authorizedSignatureName || '',
                customerSignature: customerSignature || '',
                customerSignatureName: customerSignatureName || '',
                quotationFooter: quotationFooter || '',
                updatedAt: Timestamp.now(),
                createdAt: Timestamp.now(),
                tenantId: tenantId 
            };
            
            const quotationsCol = collection(db, "quotations");
            const newDocRef = doc(quotationsCol);
            transaction.set(newDocRef, quotationData);
    
            transaction.update(quotationCountRef, { quotationnumber: nextNumber });
    
            setQuotationNumber(newQuotationNumber);
            setQuotationId(newDocRef.id);
            showNotification("Quotation and items saved successfully!", 'success');
            setIsSaved(true);
            setHasUnsavedChanges(false);
        });
    } catch (error) {
        console.error("Error saving quotation/items: ", error);
        showNotification("Error saving quotation or items. Please try again.", 'error');
    }
  };

  const generatePDF = () => {
    if (!isSaved) {
      showNotification("Please save the quotation before downloading the PDF.", 'warning');
      return;
    }
    setIsPrinting(true);
    // MODIFIED: Pass quotation-specific variables to PDF generator
    generateQuotationPDF({
      businessName: quotationBusinessName,
      businessAddress: quotationBusinessAddress,
      logo: quotationLogo,
      banner: quotationBanner,
      quotationNumber,
      quotationDate,
      validUntil,
      billTo,
      shipTo,
      items,
      totals,
      currencySymbol,
      additionalNotes,
      termsAndConditions,
      coveringLetter,
      authorizedSignature,
      authorizedSignatureName,
      customerSignature,
      customerSignatureName,
      customFooter: quotationFooter,
      isSaved,
      setIsPrinting,
      summary: [
        { label: 'Subtotal (INR)', value: totals.subtotal.toFixed(2) },
        { label: 'Discount', value: totals.totalDiscountAmount.toFixed(2) },
        { label: 'Taxable Amt (INR)', value: totals.taxableAmount.toFixed(2) },
        { label: 'CGST', value: totals.cgst.toFixed(2) },
        { label: 'SGST', value: totals.sgst.toFixed(2) },
        { label: 'IGST', value: totals.igst.toFixed(2) },
        { label: 'Total Tax (INR)', value: totals.totalTaxAmount.toFixed(2) },
      ],
    });
  };

  const handleGenerateNew = () => {
    resetForm();
  };

  const handleClearForm = () => {
    resetForm();
    setIsSaved(false); 
    setQuotationId(null);
  };

  return (
    <ThemeProvider theme={theme}>
        <SignatureUploadDialog
            open={showSignatureModal}
            onClose={handleCloseSignatureModal}
            onSave={addSignature}
        />
        <Box p={{ xs: 2, sm: 3 }} sx={{ backgroundColor: "#f0f4f8", pb: 12 }}>
            <Paper elevation={3} sx={{ padding: { xs: 2, sm: 4}, backgroundColor: "#fff", borderRadius: 2, boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)" }}>
              <div ref={contentRef}>
                {!isPrinting &&
                  <Box sx={{ mb: 4 }}>
                      <Typography variant="h6" sx={{ mb: 2, color: "#3f51b5", fontWeight: "bold", borderBottom: "2px solid #3f51b5", paddingBottom: 1, display: "inline-block" }}>
                          Covering Letter
                      </Typography>
                      <Paper elevation={1} sx={{ mb: 1 }}>
                          <Toolbar variant="dense">
                              <ButtonGroup size="small">
                                  <Button onClick={() => execCommand("bold")}> <FormatBold /> </Button>
                                  <Button onClick={() => execCommand("italic")}> <FormatItalic /> </Button>
                                  <Button onClick={() => execCommand("underline")}> <FormatUnderlined /> </Button>
                              </ButtonGroup>
                              <Divider orientation="vertical" flexItem sx={{ mx: 1.5 }} />
                              <ButtonGroup size="small">
                                  <Button onClick={() => execCommand("insertUnorderedList")}> <FormatListBulleted /> </Button>
                                  <Button onClick={() => execCommand("insertOrderedList")}> <FormatListNumbered /> </Button>
                              </ButtonGroup>
                              <Divider orientation="vertical" flexItem sx={{ mx: 1.5 }} />
                              <ButtonGroup size="small">
                                  <Button onClick={() => execCommand("justifyLeft")}> <FormatAlignLeft /> </Button>
                                  <Button onClick={() => execCommand("justifyCenter")}> <FormatAlignCenter /> </Button>
                                  <Button onClick={() => execCommand("justifyRight")}> <FormatAlignRight /> </Button>
                                  <Button onClick={() => execCommand("justifyFull")}> <FormatAlignJustify /> </Button>
                              </ButtonGroup>
                          </Toolbar>
                      </Paper>
                      <Box
                          ref={editorRef}
                          contentEditable
                          onInput={handleEditorInput}
                          sx={{
                              minHeight: 120,
                              border: "1px solid #ccc",
                              p: 2,
                              mb: 3,
                              borderRadius: 1,
                              "&:focus": { outline: "2px solid #1976d2" },
                          }}
                          suppressContentEditableWarning
                      />
                  </Box>
                }
                
                <Grid container justifyContent="space-between" alignItems="flex-start" sx={{ mb: 4 }}>
                  {quotationBanner ? (
                    <>
                      {/* Centered heading */}
                      <Grid item xs={12} sx={{ mb: 2 }}>
                        <Typography variant="h4" color="primary" sx={{ fontWeight: 700, textAlign: 'center' }}>
                          QUOTATION
                        </Typography>
                      </Grid>

                      {/* Banner full width */}
                      <Grid item xs={12}>
                        <Box sx={{ width: '100%', height: { xs: 160, sm: 200, md: 240 }, overflow: 'hidden', mb: 2, borderRadius: 1 }}>
                          <img src={quotationBanner} alt="Company Banner" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </Box>
                      </Grid>

                      {/* Centered single-line ID + date row below banner */}
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center', width: '100%', flexWrap: 'wrap', mt: 1 }}>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <TextField
                                label="Quotation ID"
                                value={quotationNumber}
                                size="small"
                                sx={{ minWidth: 160, maxWidth: 280 }}
                                InputProps={{ readOnly: true }}
                              />
                            </Box>

                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <TextField
                                type="date"
                                label="Quotation Date"
                                value={quotationDate}
                                onChange={(e) => { setQuotationDate(e.target.value); setHasUnsavedChanges(true); }}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                sx={{ maxWidth: 200 }}
                              />
                              <TextField
                                type="date"
                                label="Valid Until"
                                value={validUntil}
                                onChange={(e) => { setValidUntil(e.target.value); setHasUnsavedChanges(true); }}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                sx={{ maxWidth: 200 }}
                              />
                            </Box>
                          </Box>
                        </Box>
                      </Grid>
                    </>
                  ) : (
                    <>
                      {/* No banner: original logo + info on left, controls on right (heading centered inside right column) */}
                      <Grid item xs={12} md={7}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {quotationLogo && (
                            <Box sx={{
                              flexShrink: 0,
                              mr: 2.5,
                              width: '340px',
                              height: '220px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <img
                                src={quotationLogo}
                                alt="Company Logo"
                                style={{
                                  maxWidth: '100%',
                                  maxHeight: '100%',
                                  objectFit: 'contain'
                                }}
                              />
                            </Box>
                          )}
                          <Box>
                            <Typography variant="h6" gutterBottom sx={{ lineHeight: 1.2 }}>
                              {quotationBusinessName}
                            </Typography>
                            <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                              {quotationBusinessAddress}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={5} sx={{ pt: { xs: 2, md: 0 } }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', md: 'flex-end' }, gap: 1 }}>
                          <Typography variant="h5" color="primary" sx={{ fontWeight: 700, textAlign: 'center' }}>
                            QUOTATION
                          </Typography>

                          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1 }}>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <TextField
                                label="Quotation ID"
                                value={quotationNumber}
                                size="small"
                                sx={{ maxWidth: 200 }}
                                InputProps={{ readOnly: true }}
                              />
                            </Box>

                            <TextField
                              type="date"
                              label="Quotation Date"
                              value={quotationDate}
                              onChange={(e) => { setQuotationDate(e.target.value); setHasUnsavedChanges(true); }}
                              InputLabelProps={{ shrink: true }}
                              size="small"
                              sx={{ maxWidth: 200 }}
                            />
                          </Box>

                          {/* Valid Until on its own line for clarity */}
                          <Box sx={{ mt: 1 }}>
                            <TextField
                              type="date"
                              label="Valid Until"
                              value={validUntil}
                              onChange={(e) => { setValidUntil(e.target.value); setHasUnsavedChanges(true); }}
                              InputLabelProps={{ shrink: true }}
                              size="small"
                              sx={{ maxWidth: 200 }}
                            />
                          </Box>
                        </Box>
                      </Grid>
                    </>
                  )}
                </Grid>
                
                <Divider sx={{ my: 3, borderColor: "primary.main" }} />
                
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} md={6}>
                        <Box sx={{ backgroundColor: "#ffffff", border: "1px solid #e0e0e0", p: 2, borderRadius: 1 }}>
                            <Typography variant="h6" sx={{ mb: 1, color: "primary.main" }}>Bill To:</Typography>
                            <TextField fullWidth variant="outlined" value={billTo.name}
                                onChange={(e) => { setBillTo((prev) => ({ ...prev, name: e.target.value })); setHasUnsavedChanges(true); }}
                                placeholder="Client Name" sx={{ mb: 2 }}
                            />
                            <TextField fullWidth multiline rows={3} variant="outlined" value={billTo.address}
                                onChange={(e) => { setBillTo((prev) => ({ ...prev, address: e.target.value })); setHasUnsavedChanges(true); }}
                                placeholder="Client Address"
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box sx={{ backgroundColor: "#ffffff", border: "1px solid #e0e0e0", p: 2, borderRadius: 1 }}>
                            <Typography variant="h6" sx={{ mb: 1, color: "primary.main" }}>Ship To:</Typography>
                            <TextField fullWidth variant="outlined" value={shipTo.name}
                                onChange={(e) => { setShipTo((prev) => ({ ...prev, name: e.target.value })); setHasUnsavedChanges(true); }}
                                placeholder="Shipping Name" sx={{ mb: 2 }}
                            />
                            <TextField fullWidth multiline rows={3} variant="outlined" value={shipTo.address}
                                onChange={(e) => { setShipTo((prev) => ({ ...prev, address: e.target.value })); setHasUnsavedChanges(true); }}
                                placeholder="Shipping Address"
                            />
                        </Box>
                    </Grid>
                </Grid>
                
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-start', gap: 2 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleClick}
                        sx={{ py: 1.5, px: 3 }}
                    >
                        + Select Items
                    </Button>
                    {!isPrinting && (
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={addItem}
                            className="hide-on-print"
                        >
                            Add Manual Item Row
                        </Button>
                    )}
                </Box>

                <TableContainer component={Paper} elevation={2} sx={{ mb: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: theme.palette.primary.main }}>
                        <TableCell sx={{ color: "#fff", fontWeight: "bold", p: 1 }}>Product Name</TableCell>
                        <TableCell align="left" sx={{ color: "#fff", fontWeight: "bold", p: 1 }}>Quantity</TableCell>
                        <TableCell align="left" sx={{ color: "#fff", fontWeight: "bold", p: 1 }}>Unit Price</TableCell>
                        <TableCell align="left" sx={{ color: "#fff", fontWeight: "bold", p: 1 }}>Discount (%)</TableCell>
                        <TableCell align="left" sx={{ color: "#fff", fontWeight: "bold", p: 1 }}>Tax (%)</TableCell>
                        <TableCell align="left" sx={{ color: "#fff", fontWeight: "bold", p: 1 }}>Total</TableCell>
                        {!isPrinting && (
                          <TableCell align="left" sx={{ color: "#fff", fontWeight: "bold", p: 1 }}>Actions</TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                    {items.map((item, index) => (
                        <TableRow key={index}>
                            <TableCell sx={{p:1, verticalAlign: 'top'}}>
                                <TextField
                                    label="Product Name"
                                    value={item.description}
                                    onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                                    fullWidth
                                    required
                                    InputProps={{ readOnly: item.isLocked }}
                                    size="small"
                                />
                                {/* (warning icon moved to Actions column to avoid row height changes) */}
                            </TableCell>
                            <TableCell sx={{p:1}}>
                            <TextField
                                label="Quantity"
                                value={item.quantity}
                                onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                fullWidth
                                type="number"
                                size="small"
                                InputProps={{ readOnly: item.isLocked }}
                            />
                            </TableCell>
                            <TableCell sx={{p:1}}>
                            <TextField
                                label="Unit Price"
                                value={item.unitPrice}
                                onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                                fullWidth
                                type="number"
                                size="small"
                                InputProps={{ 
                                    readOnly: item.isLocked,
                                    startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment> 
                                }}
                            />
                            </TableCell>
                            <TableCell sx={{p:1}}>
                            <TextField
                                label="Discount"
                                value={item.discount}
                                onChange={(e) => handleItemChange(index, 'discount', e.target.value)}
                                fullWidth
                                type="number"
                                size="small"
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">%</InputAdornment> 
                                }}
                            />
                            </TableCell>
                            <TableCell sx={{p:1}}>
                            <TextField
                                label="Tax"
                                value={item.tax}
                                onChange={(e) => handleItemChange(index, 'tax', e.target.value)}
                                fullWidth
                                type="number"
                                size="small"
                                InputProps={{ 
                                    readOnly: item.isLocked,
                                    endAdornment: <InputAdornment position="end">%</InputAdornment> 
                                }}
                            />
                            </TableCell>
                            <TableCell sx={{p:1}}>
                            <TextField
                                label="Total"
                                value={item.total.toFixed(2)}
                                fullWidth
                                disabled
                                size="small"
                                InputProps={{ startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment> }}
                            />
                            </TableCell>
              {!isPrinting &&
                <TableCell sx={{p:1, display: 'flex', alignItems: 'center', gap: 1}}>
                      {(() => {
                        const unitPrice = parseFloat(item.unitPrice) || 0;
                        const discount = parseFloat(item.discount) || 0;
                        const costVal = parseFloat(item.cost) || 0;
                        const discountedUnit = unitPrice * (1 - (discount/100));
                        const isLoss = costVal > 0 && costVal > discountedUnit;
                        return (
                          <React.Fragment>
                            <IconButton color="secondary" onClick={() => deleteItem(index)}>
                              <DeleteIcon />
                            </IconButton>
                            {isLoss && (
                              <Tooltip title={`Warning: Cost (${formatCurrency(costVal, currencySymbol)}) exceeds discounted price (${formatCurrency(discountedUnit, currencySymbol)})`}>
                                <WarningAmberIcon sx={{ color: 'warning.main', ml: 0.5 }} fontSize="small" />
                              </Tooltip>
                            )}
                          </React.Fragment>
                        );
                      })()}
                </TableCell>
              }
                        </TableRow>
                    ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 2 }}>
                    <Box sx={{ width: '280px' }}>
                        <Grid container justifyContent="space-between">
                            <Grid item>Subtotal:</Grid>
                            <Grid item>{formatCurrency(totals.subtotal, currencySymbol)}</Grid>
                        </Grid>
                        <Grid container justifyContent="space-between">
                            <Grid item>Total GST:</Grid>
                            <Grid item>{formatCurrency(totals.totalTaxAmount, currencySymbol)}</Grid>
                        </Grid>
                        <Grid container justifyContent="space-between" sx={{ fontWeight: "bold", mt: 1 }}>
                            <Grid item>Grand Total:</Grid>
                            <Grid item>{formatCurrency(totals.grandTotal, currencySymbol)}</Grid>
                        </Grid>
                    </Box>
                </Box>
                
                <Typography variant="h6" sx={{ mt: 2 }}>Summary:</Typography>
                <TableContainer component={Paper} sx={{ p: 2, mb: 2 }}>
                    <Table>
                        <TableBody>
                            <TableRow sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 2, "& .MuiTableCell-root": { display: "flex", flexDirection: "column", alignItems: "start", padding: "12px", border: 'none' }}}>
                                <TableCell><Typography variant="subtitle2" color="textSecondary">Subtotal</Typography><Typography variant="h6" sx={{ mt: 1 }}>{formatCurrency(totals.subtotal, currencySymbol)}</Typography></TableCell>
                                <TableCell><Typography variant="subtitle2" color="textSecondary">Discount</Typography><Typography variant="h6" sx={{ mt: 1 }}>- {formatCurrency(totals.totalDiscountAmount, currencySymbol)}</Typography></TableCell>
                                <TableCell><Typography variant="subtitle2" color="textSecondary">Taxable Amt</Typography><Typography variant="h6" sx={{ mt: 1 }}>{formatCurrency(totals.taxableAmount, currencySymbol)}</Typography></TableCell>
                                <TableCell><Typography variant="subtitle2" color="textSecondary">CGST</Typography><Typography variant="h6" sx={{ mt: 1 }}>{formatCurrency(totals.cgst, currencySymbol)}</Typography></TableCell>
                                <TableCell><Typography variant="subtitle2" color="textSecondary">SGST</Typography><Typography variant="h6" sx={{ mt: 1 }}>{formatCurrency(totals.sgst, currencySymbol)}</Typography></TableCell>
                                <TableCell><Typography variant="subtitle2" color="textSecondary">IGST</Typography><Typography variant="h6" sx={{ mt: 1 }}>{formatCurrency(totals.igst, currencySymbol)}</Typography></TableCell>
                                <TableCell><Typography variant="subtitle2" color="textSecondary">Total Tax</Typography><Typography variant="h6" sx={{ mt: 1 }}>{formatCurrency(totals.totalTaxAmount, currencySymbol)}</Typography></TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>

              
                <Box mb={4}>
                  <Typography variant="h6" color="primary" gutterBottom>Additional Notes</Typography>
                  <TextField label="Provide any additional notes here" fullWidth multiline rows={4} value={additionalNotes} onChange={(e) => {setAdditionalNotes(e.target.value); setHasUnsavedChanges(true);}} variant="outlined" sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, padding: '10px' }, whiteSpace: 'pre-line' }}/>
                </Box>

                <Box mb={4}>
                  <Typography variant="h6" color="primary" gutterBottom>Terms and Conditions</Typography>
                    <TextField fullWidth multiline rows={4} variant="outlined" value={termsAndConditions} onChange={(e) => { setTermsAndConditions(e.target.value); setHasUnsavedChanges(true);}} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, padding: '10px' }, whiteSpace: 'pre-line' }}/>
                </Box>
                
                <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" color="primary" gutterBottom>Signatures</Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Box>
                                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: 'primary.main', fontSize: '0.95rem' }}>Authorized Signature</Typography>
                                <Paper elevation={1} sx={{ p: 1.5, border: "1px solid #ccc", borderRadius: 1, display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '80px' }}>
                                      {authorizedSignature && authorizedSignature !== "" ? (
                                      <Box sx={{ position: "relative", width: '100%', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                          <img src={authorizedSignature} alt="Authorized Signature" style={{ width: "100%", maxHeight: 60, border: '1px solid #eee', objectFit: 'contain' }} />
                                          {!isPrinting && 
                                          <IconButton size="small" sx={{ position: "absolute", top: 0, right: 0 }} onClick={() => { setAuthorizedSignature(null); setAuthorizedSignatureName(""); setHasUnsavedChanges(true); }}>
                                              <Close fontSize="small" />
                                          </IconButton>
                                          }
                                      </Box>
                                      ) : (
                                      <Box sx={{ textAlign: 'center', py: 1 }}>
                                        <CloudUploadIcon sx={{ fontSize: 36, color: '#bbb', mb: 0.5 }} />
                                        <Typography variant="body2" sx={{ color: '#666', mb: 1, fontSize: '0.85rem' }}>
                                          Upload Signature Image
                                        </Typography>
                                        <Button
                                          variant="contained"
                                          component="label"
                                          startIcon={<CloudUploadIcon />}
                                          sx={{ mb: 0.5 }}
                                          size="small"
                                        >
                                          CHOOSE IMAGE
                                          <input
                                            hidden
                                            accept="image/jpeg,image/png"
                                            type="file"
                                            onChange={(e) => {
                                              const file = e.target.files[0];
                                              if (file) openSignatureModal("authorized");
                                            }}
                                          />
                                        </Button>
                                        <Typography variant="caption" sx={{ display: 'block', color: '#999', fontSize: '0.75rem' }}>
                                          JPEG, PNG | Max 150KB
                                        </Typography>
                                      </Box>
                                      )}
                                    </Box>
                                </Paper>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box>
                                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: 'primary.main', fontSize: '0.95rem' }}>Customer Signature (Optional)</Typography>
                                <Paper elevation={1} sx={{ p: 1.5, border: "1px solid #ccc", borderRadius: 1, display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '80px' }}>
                                      {customerSignature && customerSignature !== "" ? (
                                      <Box sx={{ position: "relative", width: '100%', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                          <img src={customerSignature} alt="Customer Signature" style={{ width: "100%", maxHeight: 60, border: '1px solid #eee', objectFit: 'contain' }} />
                                          {!isPrinting && 
                                          <IconButton size="small" sx={{ position: "absolute", top: 0, right: 0 }} onClick={() => { setCustomerSignature(""); setCustomerSignatureName(""); setHasUnsavedChanges(true); }}>
                                              <Close fontSize="small" />
                                          </IconButton>
                                          }
                                      </Box>
                                      ) : (
                                      <Box sx={{ textAlign: 'center', py: 1 }}>
                                        <CloudUploadIcon sx={{ fontSize: 36, color: '#bbb', mb: 0.5 }} />
                                        <Typography variant="body2" sx={{ color: '#666', mb: 1, fontSize: '0.85rem' }}>
                                          Upload Signature Image
                                        </Typography>
                                        <Button
                                          variant="contained"
                                          component="label"
                                          startIcon={<CloudUploadIcon />}
                                          sx={{ mb: 0.5 }}
                                          size="small"
                                        >
                                          CHOOSE IMAGE
                                          <input
                                            hidden
                                            accept="image/jpeg,image/png"
                                            type="file"
                                            onChange={(e) => {
                                              const file = e.target.files[0];
                                              if (file) openSignatureModal("customer");
                                            }}
                                          />
                                        </Button>
                                        <Typography variant="caption" sx={{ display: 'block', color: '#999', fontSize: '0.75rem' }}>
                                          JPEG, PNG | Max 150KB
                                        </Typography>
                                      </Box>
                                      )}
                                    </Box>
                                </Paper>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {/* MODIFIED: Display quotation footer */}
                {quotationFooter && (
                  <Box mb={4} mt={4} sx={{ backgroundColor: '#f9f9f9', border: '1px solid #e0e0e0', borderRadius: 1, p: 2 }}>
                    <Typography variant="h6" color="primary" gutterBottom></Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line', color: 'text.secondary' }}>{quotationFooter}</Typography>
                  </Box>
                )}

              </div>
            </Paper>

            <Grid container spacing={2} sx={{ mt: 4, mb: 8, position: 'relative', zIndex: 10 }} justifyContent="center">
                <Grid item xs={12} sm={6} md={3}>
                    <Button variant="contained" color="primary" fullWidth onClick={saveQuotation} sx={{ borderRadius: 2, padding: "10px 0" }} disabled={!hasUnsavedChanges && isSaved}>
                        {isSaved ? 'Save Changes' : 'Save Quotation'}
                    </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Button variant="contained" color="secondary" fullWidth onClick={generatePDF} sx={{ borderRadius: 2, padding: "10px 0" }} disabled={!isSaved || isPrinting}>
                        {isPrinting ? 'Generating...' : 'Download PDF'}
                    </Button>
                </Grid>
                {isSaved && (
                    <Grid item xs={12} sm={6} md={3}>
                        <Button variant="outlined" color="primary" fullWidth onClick={handleGenerateNew} startIcon={<RefreshIcon />} sx={{ borderRadius: 2, padding: "10px 0" }}>
                            Generate New
                        </Button>
                    </Grid>
                )}
                <Grid item xs={12} sm={6} md={3}>
                     <Button variant="outlined" color="secondary" fullWidth onClick={handleClearForm} startIcon={<ClearAllIcon />} sx={{ borderRadius: 2, padding: "10px 0" }}>
                        Clear Form
                    </Button>
                </Grid>
            </Grid>
        </Box>

        {/* Notification Snackbar */}
        <Snackbar
          open={notification.open}
          autoHideDuration={2500}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseNotification} severity={notification.severity}>
            {notification.message}
          </Alert>
        </Snackbar>
    </ThemeProvider>
  );
};

export default QTemplate;